/* eslint-disable react/no-array-index-key */
import {TextField, css, useTheme} from '@mui/material';
import {GetStylesFunction, MaximizeButton, TranslateUtils} from '@wallaby-ui/core';
import classNames from 'classnames';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import lightFilterNodeStyles from './LightFilterNodeStyles.less';
import {FilterConfig} from './LightFormatter';
import {LightInputLabel} from './LightInputLabel';
import {ValidateError} from './LightValidator';

type Props = {
  config: FilterConfig;
  optional?: boolean;
  value: string | null | undefined;
  onChange?: (newValue: string) => void;
  isDefaultValue?: boolean;
  style?: React.CSSProperties;
  canMaximize?: boolean;
  disabled?: boolean;
  error?: ValidateError;
};
export const LightTextAreaFilter: React.FC<Props> = (props) => {
  const {config: _config, optional, value, onChange: _onChange, isDefaultValue, style, canMaximize, disabled, error} = props;
  const config = {
    ..._config,
    ui: {..._config.ui},
  };
  const prevValue = useRef(value);
  const internalValue = useRef(value);
  const [_, setInternalValueNonce] = useState(0);
  const changeInternalValue = useCallback((newValue: string | null) => {
    internalValue.current = newValue;
    setInternalValueNonce((v) => v + 1);
  }, []);
  useEffect(() => {
    if (value !== prevValue.current) {
      prevValue.current = value;
      changeInternalValue(value ?? null);
    }
  }, [prevValue, value, changeInternalValue]);
  const onChange = useCallback(() => {
    if (_onChange) _onChange(internalValue.current ?? '');
  }, [_onChange]);
  const onChangeDebounced = useMemo(
    () =>
      debounce(() => {
        onChange();
      }, 400),
    [onChange]
  );
  const onInput = useCallback(
    (event) => {
      changeInternalValue(event.target.value);
      onChangeDebounced();
    },
    [changeInternalValue, onChangeDebounced]
  );
  const intl = useIntl();
  const translatedPlaceholder = config.ui.placeholder ? TranslateUtils.translate(config.ui.placeholder, intl) : undefined;
  const theme = useTheme();
  let isOccupied = !isDefaultValue;
  if (isDefaultValue === undefined) isOccupied = Boolean(value);
  const muiStyles = getStyles({theme, params: {isOccupied}});
  const [inputMaximized, setInputMaximized] = useState(false);
  return (
    <span
      className={classNames(lightFilterNodeStyles.dlFilterNodeContent, 'dlFilterNode')}
      data-name={config.name}
      data-input-can-maximize={canMaximize}
      data-input-maximized={inputMaximized}
      css={muiStyles.filterWrapper}
      style={style}
    >
      <TextField
        value={internalValue.current ?? ''}
        variant='outlined'
        multiline
        data-occupied={isOccupied}
        data-disabled={Boolean(disabled)}
        data-error={Boolean(error?.bool)}
        disabled={disabled}
        label={<LightInputLabel text={config.ui.label} optional={optional} intl={intl} />}
        placeholder={translatedPlaceholder}
        onInput={onInput}
        onChange={onChange}
        rows={inputMaximized ? undefined : 5}
        minRows={inputMaximized ? 5 : undefined}
        fullWidth
        error={error?.bool}
        helperText={error?.string}
        InputProps={{
          sx: {
            height: '100%',
            background: 'white',
            alignItems: 'start',
          },
          endAdornment: canMaximize && (
            <MaximizeButton
              maximized={inputMaximized}
              toggleMaximized={() => {
                setInputMaximized(!inputMaximized);
              }}
              colors={{
                default: {background: 'none', fill: 'var(--dlLightIconButtonFill)'},
                hover: {background: 'none', fill: 'var(--dlLightIconButtonFill)'},
                selected: {background: 'none', fill: 'var(--dlLightIconButtonFill)'},
                selectedHover: {background: 'none', fill: 'var(--dlLightIconButtonFill)'},
                active: {background: 'none', fill: 'var(--dlLightIconButtonFill)'},
              }}
            />
          ),
        }}
      />
    </span>
  );
};
const getStyles: GetStylesFunction = (options) => {
  return {
    filterWrapper: css`
      max-width: 362px;
      padding: 4px 0;
      & .MuiInputLabel-root {
        z-index: 1;
      }
      & .MuiInputLabel-root[data-shrink='true'] {
        color: var(--dlTheme-600);
      }
    `,
  };
};
